import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabase';
import { 
  notifyEntityCreated, 
  notifyEntityUpdated, 
  notifyEntityFinished 
} from '../utils/notifications';
import { 
  Client, 
  Supplier, 
  Invoice, 
  InvoiceItem, 
  Payment, 
  Production, 
  Sample, 
  SampleTask, 
  ProductionTask, 
  Express, 
  ExpressItem, 
  ExpressStock, 
  ExpressStockMovement, 
  ExpressSample, 
  ExpressSampleItem,
  TodoItem,
  ChatMessage
} from '../types';

interface AppState {
  // UI state
  darkMode: boolean;
  toggleDarkMode: () => void;
  error: string | null;
  showError: boolean;
  setError: (error: string | null) => void;
  clearError: () => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;

  // Data
  clients: Client[];
  suppliers: Supplier[];
  invoices: Invoice[];
  payments: Payment[];
  productions: Production[];
  productionTasks: ProductionTask[];
  samples: Sample[];
  sampleTasks: SampleTask[];
  express: Express[];
  expressItems: ExpressItem[];
  expressStock: ExpressStock[];
  expressStockMovements: ExpressStockMovement[];
  expressSamples: ExpressSample[];
  expressSampleItems: ExpressSampleItem[];
  todoItems: TodoItem[];
  chatMessages: ChatMessage[];
  expenseCategories: any[];
  expenses: any[];
  currentUser: any | null;
  setCurrentUser: (user: any | null) => void;
  savedAddresses: any[];

  // Fetch functions
  fetchClients: () => Promise<void>;
  fetchSuppliers: () => Promise<void>;
  fetchInvoices: () => Promise<void>;
  fetchPayments: () => Promise<void>;
  fetchProductions: () => Promise<void>;
  fetchProductionTasks: () => Promise<void>;
  fetchSamples: () => Promise<void>;
  fetchSampleTasks: () => Promise<void>;
  fetchExpress: () => Promise<void>;
  fetchExpressItems: () => Promise<void>;
  fetchExpressStock: () => Promise<void>;
  fetchExpressStockMovements: () => Promise<void>;
  fetchExpressSamples: () => Promise<void>;
  fetchExpressSampleItems: () => Promise<void>;
  fetchTodoItems: () => Promise<void>;
  fetchChatMessages: (channel: string) => Promise<void>;
  fetchExpenseCategories: () => Promise<void>;
  fetchExpenses: () => Promise<void>;
  fetchSavedAddresses: () => Promise<void>;

  // CRUD functions
  // Clients
  addClient: (client: Omit<Client, 'id' | 'balance'>) => Promise<Client>;
  updateClient: (id: string, client: Omit<Client, 'id' | 'balance'>) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;

  // Suppliers
  addSupplier: (supplier: Omit<Supplier, 'id' | 'balance'>) => Promise<Supplier>;
  updateSupplier: (id: string, supplier: Omit<Supplier, 'id' | 'balance'>) => Promise<void>;
  deleteSupplier: (id: string) => Promise<void>;

  // Invoices
  addInvoice: (invoice: Omit<Invoice, 'id'>, items: Omit<InvoiceItem, 'id' | 'invoice_id'>[]) => Promise<void>;
  updateInvoice: (id: string, invoice: Partial<Invoice>) => Promise<void>;
  deleteInvoice: (id: string) => Promise<void>;
  voidInvoice: (id: string) => Promise<void>;

  // Payments
  addPayment: (payment: Omit<Payment, 'id'>) => Promise<void>;
  updatePayment: (id: string, payment: Partial<Payment>) => Promise<void>;
  deletePayment: (id: string) => Promise<void>;
  voidPayment: (id: string) => Promise<void>;

  // Productions
  addProduction: (production: Omit<Production, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateProduction: (id: string, production: Partial<Production>) => Promise<void>;
  deleteProduction: (id: string) => Promise<void>;

  // Production Tasks
  addProductionTask: (task: Omit<ProductionTask, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateProductionTask: (id: string, task: Partial<ProductionTask>) => Promise<void>;
  deleteProductionTask: (id: string) => Promise<void>;

  // Samples
  addSample: (sample: Omit<Sample, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateSample: (id: string, sample: Partial<Sample>) => Promise<void>;
  deleteSample: (id: string) => Promise<void>;

  // Sample Tasks
  addSampleTask: (task: Omit<SampleTask, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateSampleTask: (id: string, task: Partial<SampleTask>) => Promise<void>;
  deleteSampleTask: (id: string) => Promise<void>;

  // Express
  addExpress: (express: Omit<Express, 'id' | 'created_at' | 'updated_at'>, items: Omit<ExpressItem, 'id' | 'express_id' | 'created_at'>[]) => Promise<string>;
  updateExpress: (id: string, express: Partial<Express> & { items?: Omit<ExpressItem, 'id' | 'express_id' | 'created_at'>[] }) => Promise<void>;
  deleteExpress: (id: string) => Promise<void>;

  // Express Stock
  addExpressStock: (stock: Omit<ExpressStock, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateExpressStock: (id: string, stock: Partial<ExpressStock>) => Promise<void>;
  deleteExpressStock: (id: string) => Promise<void>;

  // Express Stock Movements
  addExpressStockMovement: (movement: Omit<ExpressStockMovement, 'id' | 'created_at'>) => Promise<void>;

  // Express Samples
  addExpressSample: (sample: Omit<ExpressSample, 'id' | 'created_at' | 'updated_at'>, items: Omit<ExpressSampleItem, 'id' | 'express_sample_id' | 'created_at'>[]) => Promise<string>;
  updateExpressSample: (id: string, sample: Partial<ExpressSample> & { items?: Omit<ExpressSampleItem, 'id' | 'express_sample_id' | 'created_at'>[] }) => Promise<void>;
  deleteExpressSample: (id: string) => Promise<void>;

  // Todo Items
  addTodoItem: (item: Omit<TodoItem, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateTodoItem: (id: string, item: Partial<TodoItem>) => Promise<void>;
  deleteTodoItem: (id: string) => Promise<void>;
  toggleTodoItemCompletion: (id: string, completed: boolean) => Promise<void>;

  // Chat Messages
  addChatMessage: (message: Omit<ChatMessage, 'id' | 'created_at'>) => Promise<void>;
  deleteChatMessage: (id: string) => Promise<void>;
  toggleChatMessageReaction: (messageId: string, userId: string) => Promise<void>;

  // Expenses
  addExpense: (expense: Omit<any, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateExpense: (id: string, expense: Partial<any>) => Promise<void>;
  deleteExpense: (id: string) => Promise<void>;

  // Saved Addresses
  addSavedAddress: (address: Omit<any, 'id' | 'created_at' | 'updated_at'>) => Promise<any>;
  updateSavedAddress: (id: string, address: Partial<any>) => Promise<void>;
  deleteSavedAddress: (id: string) => Promise<void>;

  // Utility functions
  calculateEntityBalance: (entityId: string, entityType: 'client' | 'supplier') => number;
}

export const useAppStore = create<AppState>((set, get) => ({
  // UI state
  darkMode: localStorage.getItem('darkMode') === 'true',
  toggleDarkMode: () => {
    const newDarkMode = !get().darkMode;
    localStorage.setItem('darkMode', newDarkMode.toString());
    set({ darkMode: newDarkMode });
  },
  error: null,
  showError: false,
  setError: (error) => set({ error, showError: !!error }),
  clearError: () => set({ error: null, showError: false }),
  isLoading: false,
  setIsLoading: (isLoading) => set({ isLoading }),

  // Data
  clients: [],
  suppliers: [],
  invoices: [],
  payments: [],
  productions: [],
  productionTasks: [],
  samples: [],
  sampleTasks: [],
  express: [],
  expressItems: [],
  expressStock: [],
  expressStockMovements: [],
  expressSamples: [],
  expressSampleItems: [],
  todoItems: [],
  chatMessages: [],
  expenseCategories: [],
  expenses: [],
  currentUser: null,
  setCurrentUser: (user) => set({ currentUser: user }),
  savedAddresses: [],

  // Fetch functions
  fetchClients: async () => {
    try {
      const { data, error } = await supabase.from('clients').select('*').order('name');
      if (error) throw error;
      set({ clients: data || [] });
    } catch (error: any) {
      console.error('Error fetching clients:', error);
      get().setError(error.message);
    }
  },

  fetchSuppliers: async () => {
    try {
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .order('name');
      
      if (error) throw error;
      set({ suppliers: data || [] });
    } catch (error: any) {
      console.error('Error fetching suppliers:', error);
      get().setError(error.message);
    }
  },

  fetchInvoices: async () => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          items:invoice_items(*)
        `)
        .order('date', { ascending: false });
      
      if (error) throw error;
      set({ invoices: data || [] });
    } catch (error: any) {
      console.error('Error fetching invoices:', error);
      get().setError(error.message);
    }
  },

  fetchPayments: async () => {
    try {
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .order('date', { ascending: false });
      
      if (error) throw error;
      set({ payments: data || [] });
    } catch (error: any) {
      console.error('Error fetching payments:', error);
      get().setError(error.message);
    }
  },

  fetchProductions: async () => {
    try {
      const { data, error } = await supabase
        .from('productions')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      set({ productions: data || [] });
    } catch (error: any) {
      console.error('Error fetching productions:', error);
      get().setError(error.message);
    }
  },

  fetchProductionTasks: async () => {
    try {
      const { data, error } = await supabase
        .from('production_tasks')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      set({ productionTasks: data || [] });
    } catch (error: any) {
      console.error('Error fetching production tasks:', error);
      get().setError(error.message);
    }
  },

  fetchSamples: async () => {
    try {
      const { data, error } = await supabase
        .from('samples')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      set({ samples: data || [] });
    } catch (error: any) {
      console.error('Error fetching samples:', error);
      get().setError(error.message);
    }
  },

  fetchSampleTasks: async () => {
    try {
      const { data, error } = await supabase
        .from('sample_tasks')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      set({ sampleTasks: data || [] });
    } catch (error: any) {
      console.error('Error fetching sample tasks:', error);
      get().setError(error.message);
    }
  },

  fetchExpress: async () => {
    try {
      const { data, error } = await supabase
        .from('express')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      set({ express: data || [] });
    } catch (error: any) {
      console.error('Error fetching express orders:', error);
      // Provide more specific error messages for network issues
      const errorMessage = error.message?.includes('fetch') || error.name === 'TypeError'
        ? 'Error de conexión al cargar órdenes express. Verifique su conexión a internet.'
        : error.message;
      get().setError(errorMessage);
      throw error; // Re-throw to allow caller to handle
    }
  },

  fetchExpressItems: async () => {
    try {
      const { data, error } = await supabase
        .from('express_items')
        .select('*');
      
      if (error) throw error;
      set({ expressItems: data || [] });
    } catch (error: any) {
      console.error('Error fetching express items:', error);
      get().setError(error.message);
    }
  },

  fetchExpressStock: async () => {
    try {
      const { data, error } = await supabase
        .from('express_stock')
        .select('*')
        .order('product_type, color, size');
      
      if (error) throw error;
      set({ expressStock: data || [] });
    } catch (error: any) {
      console.error('Error fetching express stock:', error);
      get().setError(error.message);
    }
  },

  fetchExpressStockMovements: async () => {
    try {
      const { data, error } = await supabase
        .from('express_stock_movements')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      set({ expressStockMovements: data || [] });
    } catch (error: any) {
      console.error('Error fetching express stock movements:', error);
      // Provide more specific error messages for network issues
      const errorMessage = error.message?.includes('fetch') || error.name === 'TypeError'
        ? 'Error de conexión al cargar movimientos de stock express. Verifique su conexión a internet.'
        : error.message;
      get().setError(errorMessage);
      throw error; // Re-throw to allow caller to handle
    }
  },

  fetchExpressSamples: async () => {
    try {
      const { data, error } = await supabase
        .from('express_samples')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      set({ expressSamples: data || [] });
    } catch (error: any) {
      console.error('Error fetching express samples:', error);
      get().setError(error.message);
    }
  },

  fetchExpressSampleItems: async () => {
    try {
      const { data, error } = await supabase
        .from('express_sample_items')
        .select('*');
      
      if (error) throw error;
      set({ expressSampleItems: data || [] });
    } catch (error: any) {
      console.error('Error fetching express sample items:', error);
      get().setError(error.message);
    }
  },

  fetchTodoItems: async () => {
    try {
      const { data, error } = await supabase
        .from('todo_items')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      set({ todoItems: data || [] });
    } catch (error: any) {
      console.error('Error fetching todo items:', error);
      get().setError(error.message);
    }
  },

  fetchChatMessages: async (channel: string) => {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('channel', channel)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      set({ chatMessages: data || [] });
    } catch (error: any) {
      console.error('Error fetching chat messages:', error);
      get().setError(error.message);
    }
  },

  fetchExpenseCategories: async () => {
    try {
      const { data, error } = await supabase
        .from('expenses_categories')
        .select('*')
        .order('name');
      
      if (error) throw error;
      set({ expenseCategories: data || [] });
    } catch (error: any) {
      console.error('Error fetching expense categories:', error);
      get().setError(error.message);
    }
  },

  fetchExpenses: async () => {
    try {
      const { data, error } = await supabase
        .from('expenses')
        .select('*')
        .order('date', { ascending: false });
      
      if (error) throw error;
      set({ expenses: data || [] });
    } catch (error: any) {
      console.error('Error fetching expenses:', error);
      get().setError(error.message);
    }
  },

  fetchSavedAddresses: async () => {
    try {
      const { data, error } = await supabase.from('saved_addresses').select('*').order('name');
      if (error) throw error;
      set({ savedAddresses: data || [] });
    } catch (error: any) {
      console.error('Error fetching saved addresses:', error);
      get().setError(error.message);
    }
  },

  // CRUD functions
  // Clients
  addClient: async (client) => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .insert([client])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        clients: [...state.clients, data]
      }));
      
      return data;
    } catch (error: any) {
      console.error('Error adding client:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateClient: async (id, client) => {
    try {
      const { error } = await supabase
        .from('clients')
        .update(client)
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        clients: state.clients.map(c => c.id === id ? { ...c, ...client } : c)
      }));
    } catch (error: any) {
      console.error('Error updating client:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteClient: async (id) => {
    try {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        clients: state.clients.filter(c => c.id !== id)
      }));
    } catch (error: any) {
      console.error('Error deleting client:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Suppliers
  addSupplier: async (supplier) => {
    try {
      const { data, error } = await supabase
        .from('suppliers')
        .insert([supplier])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        suppliers: [...state.suppliers, data]
      }));
      
      return data;
    } catch (error: any) {
      console.error('Error adding supplier:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateSupplier: async (id, supplier) => {
    try {
      const { error } = await supabase
        .from('suppliers')
        .update(supplier)
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        suppliers: state.suppliers.map(s => s.id === id ? { ...s, ...supplier } : s)
      }));
    } catch (error: any) {
      console.error('Error updating supplier:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteSupplier: async (id) => {
    try {
      const { error } = await supabase
        .from('suppliers')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        suppliers: state.suppliers.filter(s => s.id !== id)
      }));
    } catch (error: any) {
      console.error('Error deleting supplier:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Invoices
  addInvoice: async (invoice, items) => {
    try {
      // Insert invoice
      const { data: invoiceData, error: invoiceError } = await supabase
        .from('invoices')
        .insert([invoice])
        .select()
        .single();
      
      if (invoiceError) throw invoiceError;
      
      // Insert invoice items
      const itemsWithInvoiceId = items.map(item => ({
        ...item,
        invoice_id: invoiceData.id
      }));
      
      const { error: itemsError } = await supabase
        .from('invoice_items')
        .insert(itemsWithInvoiceId);
      
      if (itemsError) throw itemsError;
      
      // Update local state
      set(state => ({
        invoices: [...state.invoices, { ...invoiceData, items: itemsWithInvoiceId }]
      }));
    } catch (error: any) {
      console.error('Error adding invoice:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateInvoice: async (id, invoice) => {
    try {
      const { error } = await supabase
        .from('invoices')
        .update(invoice)
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        invoices: state.invoices.map(inv => 
          inv.id === id ? { ...inv, ...invoice } : inv
        )
      }));
    } catch (error: any) {
      console.error('Error updating invoice:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteInvoice: async (id) => {
    try {
      const { error } = await supabase
        .from('invoices')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        invoices: state.invoices.filter(inv => inv.id !== id)
      }));
    } catch (error: any) {
      console.error('Error deleting invoice:', error);
      get().setError(error.message);
      throw error;
    }
  },

  voidInvoice: async (id) => {
    try {
      // Call the void_invoice function
      const { error } = await supabase.rpc('void_invoice', { p_invoice_id: id });
      
      if (error) throw error;
      
      // Update local state
      set(state => ({
        invoices: state.invoices.map(inv => 
          inv.id === id ? { ...inv, voided: true, voided_at: new Date().toISOString() } : inv
        ),
        // Also update payments for this invoice
        payments: state.payments.map(payment => {
          const paymentInvoice = state.invoices.find(inv => inv.id === payment.invoice_id);
          if (paymentInvoice && paymentInvoice.id === id) {
            return { ...payment, voided: true, voided_at: new Date().toISOString() };
          }
          return payment;
        })
      }));
    } catch (error: any) {
      console.error('Error voiding invoice:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Payments
  addPayment: async (payment) => {
    try {
      const { data, error } = await supabase
        .from('payments')
        .insert([payment])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        payments: [...state.payments, data]
      }));
    } catch (error: any) {
      console.error('Error adding payment:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updatePayment: async (id, payment) => {
    try {
      const { error } = await supabase
        .from('payments')
        .update(payment)
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        payments: state.payments.map(p => 
          p.id === id ? { ...p, ...payment } : p
        )
      }));
    } catch (error: any) {
      console.error('Error updating payment:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deletePayment: async (id) => {
    try {
      // Get the payment before deleting it to update invoice balance
      const { payments, invoices } = get();
      const payment = payments.find(p => p.id === id);
      
      const { error } = await supabase
        .from('payments')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      // If this payment was associated with an invoice, update the invoice balance
      if (payment && payment.invoice_id) {
        const invoice = invoices.find(inv => inv.id === payment.invoice_id);
        if (invoice) {
          // Restore the invoice balance by adding back the payment amount
          const newBalance = invoice.balance + payment.amount;
          
          // Update the invoice balance in the database
          const { error: updateError } = await supabase
            .from('invoices')
            .update({ balance: newBalance })
            .eq('id', payment.invoice_id);
          
          if (updateError) throw updateError;
          
          // Update local state
          set(state => ({
            invoices: state.invoices.map(inv => 
              inv.id === payment.invoice_id ? { ...inv, balance: newBalance } : inv
            )
          }));
        }
      }
      
      set(state => ({
        payments: state.payments.filter(p => p.id !== id)
      }));
    } catch (error: any) {
      console.error('Error deleting payment:', error);
      get().setError(error.message);
      throw error;
    }
  },

  voidPayment: async (id) => {
    try {
      // Get the payment before voiding it to update invoice balance
      const payment = payments.find(p => p.id === id);
      
      // Call the void_payment function
      const { error } = await supabase.rpc('void_payment', { p_payment_id: id });
      
      if (error) throw error;
      
      // If this payment was associated with an invoice, update the invoice balance
      if (payment && payment.invoice_id && !payment.voided) {
        const invoice = invoices.find(inv => inv.id === payment.invoice_id);
        if (invoice) {
          // Restore the invoice balance by adding back the payment amount
          const newBalance = invoice.balance + payment.amount;
          
          // Update the invoice balance in the database
          const { error: updateError } = await supabase
            .from('invoices')
            .update({ balance: newBalance })
            .eq('id', payment.invoice_id);
          
          if (updateError) throw updateError;
          
          // Update local state
          set(state => ({
            invoices: state.invoices.map(inv => 
              inv.id === payment.invoice_id ? { ...inv, balance: newBalance } : inv
            )
          }));
        }
      }
      
      // Update local state
      set(state => ({
        payments: state.payments.map(p => 
          p.id === id ? { ...p, voided: true, voided_at: new Date().toISOString() } : p
        )
      }));
    } catch (error: any) {
      console.error('Error voiding payment:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Productions
  addProduction: async (production) => {
    try {
      const { data, error } = await supabase
        .from('productions')
        .insert([production])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        productions: [...state.productions, data]
      }));

      // Enviar notificación de nueva producción creada
      const client = get().clients.find(c => c.id === production.client_id);
      await notifyEntityCreated('production', data, client?.name);
    } catch (error: any) {
      console.error('Error adding production:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateProduction: async (id, production) => {
    try {
      // Obtener el estado anterior para detectar cambios
      const previousProduction = get().productions.find(p => p.id === id);
      const previousStatus = previousProduction?.status;

      const { error } = await supabase
        .from('productions')
        .update(production)
        .eq('id', id);
      
      if (error) throw error;
      
      const updatedProduction = { ...previousProduction, ...production };
      
      set(state => ({
        productions: state.productions.map(p => 
          p.id === id ? updatedProduction : p
        )
      }));

      // Enviar notificación de actualización
      const client = get().clients.find(c => c.id === updatedProduction.client_id);
      await notifyEntityUpdated('production', updatedProduction, client?.name, previousStatus);
    } catch (error: any) {
      console.error('Error updating production:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteProduction: async (id) => {
    try {
      const { error } = await supabase
        .from('productions')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        productions: state.productions.filter(p => p.id !== id)
      }));
    } catch (error: any) {
      console.error('Error deleting production:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Production Tasks
  addProductionTask: async (task) => {
    try {
      const { data, error } = await supabase
        .from('production_tasks')
        .insert([task])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        productionTasks: [...state.productionTasks, data]
      }));
    } catch (error: any) {
      console.error('Error adding production task:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateProductionTask: async (id, task) => {
    try {
      const { error } = await supabase
        .from('production_tasks')
        .update(task)
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        productionTasks: state.productionTasks.map(t => 
          t.id === id ? { ...t, ...task } : t
        )
      }));
    } catch (error: any) {
      console.error('Error updating production task:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteProductionTask: async (id) => {
    try {
      const { error } = await supabase
        .from('production_tasks')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        productionTasks: state.productionTasks.filter(t => t.id !== id)
      }));
    } catch (error: any) {
      console.error('Error deleting production task:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Samples
  addSample: async (sample) => {
    try {
      const { data, error } = await supabase
        .from('samples')
        .insert([sample])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        samples: [...state.samples, data]
      }));

      // Enviar notificación de nueva muestra creada
      const client = get().clients.find(c => c.id === sample.client_id);
      await notifyEntityCreated('sample', data, client?.name);
    } catch (error: any) {
      console.error('Error adding sample:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateSample: async (id, sample) => {
    try {
      // Obtener el estado anterior para detectar cambios
      const previousSample = get().samples.find(s => s.id === id);
      const previousStatus = previousSample?.status;

      const { error } = await supabase
        .from('samples')
        .update(sample)
        .eq('id', id);
      
      if (error) throw error;
      
      const updatedSample = { ...previousSample, ...sample };
      
      set(state => ({
        samples: state.samples.map(s => 
          s.id === id ? updatedSample : s
        )
      }));

      // Enviar notificación de actualización
      const client = get().clients.find(c => c.id === updatedSample.client_id);
      await notifyEntityUpdated('sample', updatedSample, client?.name, previousStatus);
    } catch (error: any) {
      console.error('Error updating sample:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteSample: async (id) => {
    try {
      const { error } = await supabase
        .from('samples')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        samples: state.samples.filter(s => s.id !== id)
      }));
    } catch (error: any) {
      console.error('Error deleting sample:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Sample Tasks
  addSampleTask: async (task) => {
    try {
      const { data, error } = await supabase
        .from('sample_tasks')
        .insert([task])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        sampleTasks: [...state.sampleTasks, data]
      }));
    } catch (error: any) {
      console.error('Error adding sample task:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateSampleTask: async (id, task) => {
    try {
      const { error } = await supabase
        .from('sample_tasks')
        .update(task)
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        sampleTasks: state.sampleTasks.map(t => 
          t.id === id ? { ...t, ...task } : t
        )
      }));
    } catch (error: any) {
      console.error('Error updating sample task:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteSampleTask: async (id) => {
    try {
      const { error } = await supabase
        .from('sample_tasks')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        sampleTasks: state.sampleTasks.filter(t => t.id !== id)
      }));
    } catch (error: any) {
      console.error('Error deleting sample task:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Express
addExpress: async (order, items) => {
  try {
    /* 1) Insertar la orden y pedir la fila completa para obtener el id real */
    const { data: savedOrder, error: orderErr } = await supabase
      .from('express')
      .insert([order])
      .select()          // ← obliga a devolver la fila insertada
      .single();         // ← la fila viene como objeto, no array

    if (orderErr) throw orderErr;

    /* 2) Insertar los ítems (si hay) ya con el id real */
    if (items && items.length) {
      const itemsWithId = items.map(itm => ({
        ...itm,
        express_id: savedOrder.id,
      }));

      const { error: itemsErr } = await supabase
        .from('express_items')
        .insert(itemsWithId);

      if (itemsErr) throw itemsErr;
    }

    /* 3) Como todo salió bien, actualizamos el estado global */
    set(state => ({
      express: [...state.express, savedOrder],
    }));

    /* 4) Enviar notificación de nueva orden express creada */
    const client = get().clients.find(c => c.id === order.client_id);
    await notifyEntityCreated('express_order', savedOrder, client?.name);

    /* 5) Devolver el id real a la modal */
    return savedOrder.id;
  } catch (error) {
    console.error('Error adding express order:', error);
    get().setError(error.message);
    throw error; // la modal mostrará ErrorModal y no limpiará el formulario
  }
},

  updateExpress: async (id, express) => {
    try {
      // Extract items from the express object
      const { items, ...expressData } = express;
      
      // Update express order
      const { error: expressError } = await supabase
        .from('express')
        .update(expressData)
        .eq('id', id);
      
      if (expressError) throw expressError;
      
      // Update express items if provided
      if (items && items.length > 0) {
        // First, delete existing items
        const { error: deleteError } = await supabase
          .from('express_items')
          .delete()
          .eq('express_id', id);
        
        if (deleteError) throw deleteError;
        
        // Then, insert new items
        const itemsWithExpressId = items.map(item => ({
          ...item,
          express_id: id
        }));
        
        const { error: itemsError } = await supabase
          .from('express_items')
          .insert(itemsWithExpressId);
        
        if (itemsError) throw itemsError;
        
        // Update local state
        set(state => ({
          express: state.express.map(e => 
            e.id === id ? { ...e, ...expressData } : e
          ),
          expressItems: [
            ...state.expressItems.filter(item => item.express_id !== id),
            ...itemsWithExpressId.map(item => ({
              ...item,
              id: uuidv4(), // Temporary ID until we fetch the real ones
              created_at: new Date().toISOString()
            }))
          ]
        }));
      } else {
        // Update local state without changing items
        set(state => ({
          express: state.express.map(e => 
            e.id === id ? { ...e, ...expressData } : e
          )
        }));
      }
    } catch (error: any) {
      console.error('Error updating express order:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteExpress: async (id) => {
    try {
      const { error } = await supabase
        .from('express')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        express: state.express.filter(e => e.id !== id),
        expressItems: state.expressItems.filter(item => item.express_id !== id)
      }));
    } catch (error: any) {
      console.error('Error deleting express order:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Express Stock
  addExpressStock: async (stock) => {
    try {
      const { data, error } = await supabase
        .from('express_stock')
        .insert([stock])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        expressStock: [...state.expressStock, data]
      }));
    } catch (error: any) {
      console.error('Error adding express stock:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateExpressStock: async (id, stock) => {
    try {
      const { error } = await supabase
        .from('express_stock')
        .update(stock)
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        expressStock: state.expressStock.map(s => 
          s.id === id ? { ...s, ...stock } : s
        )
      }));
    } catch (error: any) {
      console.error('Error updating express stock:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteExpressStock: async (id) => {
    try {
      const { error } = await supabase
        .from('express_stock')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        expressStock: state.expressStock.filter(s => s.id !== id)
      }));
    } catch (error: any) {
      console.error('Error deleting express stock:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Express Stock Movements
  addExpressStockMovement: async (movement) => {
    try {
      const { data, error } = await supabase
        .from('express_stock_movements')
        .insert([movement])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        expressStockMovements: [...state.expressStockMovements, data]
      }));
    } catch (error: any) {
      console.error('Error adding express stock movement:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Express Samples
  addExpressSample: async (sample, items) => {
    try {
      // Insert express sample
      const { data: sampleData, error: sampleError } = await supabase
        .from('express_samples')
        .insert([sample])
        .select()
        .single();
      
      if (sampleError) throw sampleError;
      
      // Insert express sample items
      if (items && items.length > 0) {
        const itemsWithSampleId = items.map(item => ({
          ...item,
          express_sample_id: sampleData.id
        }));
        
        const { error: itemsError } = await supabase
          .from('express_sample_items')
          .insert(itemsWithSampleId);
        
        if (itemsError) throw itemsError;
        
        // Update local state with items
        const newItems = itemsWithSampleId.map(item => ({
          ...item,
          id: uuidv4(), // Temporary ID until we fetch the real ones
          created_at: new Date().toISOString()
        }));
        
        set(state => ({
          expressSamples: [
  ...state.expressSamples,
  { ...sampleData, file_names: sample.file_names }                // ← NUEVO
],
          expressSampleItems: [...state.expressSampleItems, ...newItems]
        }));
      } else {
        // Update local state without items
        set(state => ({
          expressSamples: [...state.expressSamples, sampleData]
        }));
      }

      // Enviar notificación de nueva muestra express creada
      const client = get().clients.find(c => c.id === sample.client_id);
      await notifyEntityCreated('express_sample', sampleData, client?.name);
      
      return sampleData.id;
    } catch (error: any) {
      console.error('Error adding express sample:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateExpressSample: async (id, sample) => {
    try {
      // Extract items from the sample object
      const { items, ...sampleData } = sample;
      
      // Update express sample
      const { error: sampleError } = await supabase
        .from('express_samples')
        .update(sampleData)
        .eq('id', id);
      
      if (sampleError) throw sampleError;
      
      // Update express sample items if provided
      if (items && items.length > 0) {
        // First, delete existing items
        const { error: deleteError } = await supabase
          .from('express_sample_items')
          .delete()
          .eq('express_sample_id', id);
        
        if (deleteError) throw deleteError;
        
        // Then, insert new items
        const itemsWithSampleId = items.map(item => ({
          ...item,
          express_sample_id: id
        }));
        
        const { error: itemsError } = await supabase
          .from('express_sample_items')
          .insert(itemsWithSampleId);
        
        if (itemsError) throw itemsError;
        
        // Update local state
        set(state => ({
          expressSamples: state.expressSamples.map(s => 
            s.id === id ? { ...s, ...sampleData, file_names: sample.file_names } : s
          ),
          expressSampleItems: [
            ...state.expressSampleItems.filter(item => item.express_sample_id !== id),
            ...itemsWithSampleId.map(item => ({
              ...item,
              id: uuidv4(), // Temporary ID until we fetch the real ones
              created_at: new Date().toISOString()
            }))
          ]
        }));
      } else {
        // Update local state without changing items
        set(state => ({
          expressSamples: state.expressSamples.map(s => 
            s.id === id ? { ...s, ...sampleData, file_names: sample.file_names } : s
          )
        }));
      }
    } catch (error: any) {
      console.error('Error updating express sample:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteExpressSample: async (id) => {
    try {
      const { error } = await supabase
        .from('express_samples')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        expressSamples: state.expressSamples.filter(s => s.id !== id),
        expressSampleItems: state.expressSampleItems.filter(item => item.express_sample_id !== id)
      }));
    } catch (error: any) {
      console.error('Error deleting express sample:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Todo Items
  addTodoItem: async (item) => {
    try {
      const { data, error } = await supabase
        .from('todo_items')
        .insert([item])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        todoItems: [...state.todoItems, data]
      }));
      
      // Update order in localStorage
      const savedOrder = localStorage.getItem('todo_items_order');
      if (savedOrder) {
        try {
          const orderArray = JSON.parse(savedOrder);
          // Add new task ID at the beginning (newest first)
          const newOrder = [data.id, ...orderArray];
          localStorage.setItem('todo_items_order', JSON.stringify(newOrder));
        } catch (e) {
          console.error('Error updating todo order:', e);
        }
      } else {
        // Initialize order with just this task
        localStorage.setItem('todo_items_order', JSON.stringify([data.id]));
      }
      
      return data;
    } catch (error: any) {
      console.error('Error adding todo item:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateTodoItem: async (id, item) => {
    try {
      const { error } = await supabase
        .from('todo_items')
        .update(item)
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        todoItems: state.todoItems.map(t => 
          t.id === id ? { ...t, ...item } : t
        )
      }));
    } catch (error: any) {
      console.error('Error updating todo item:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteTodoItem: async (id) => {
    try {
      const { error } = await supabase
        .from('todo_items')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        todoItems: state.todoItems.filter(t => t.id !== id)
      }));
    } catch (error: any) {
      console.error('Error deleting todo item:', error);
      get().setError(error.message);
      throw error;
    }
  },

  toggleTodoItemCompletion: async (id, completed) => {
    try {
      const { error } = await supabase
        .from('todo_items')
        .update({ completed })
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        todoItems: state.todoItems.map(t => 
          t.id === id ? { ...t, completed } : t
        )
      }));
    } catch (error: any) {
      console.error('Error toggling todo item completion:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Chat Messages
  addChatMessage: async (message) => {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .insert([message])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        chatMessages: [...state.chatMessages, data]
      }));
    } catch (error: any) {
      console.error('Error adding chat message:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteChatMessage: async (id) => {
    try {
      const { error } = await supabase
        .from('chat_messages')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        chatMessages: state.chatMessages.filter(m => m.id !== id)
      }));
    } catch (error: any) {
      console.error('Error deleting chat message:', error);
      get().setError(error.message);
      throw error;
    }
  },

  toggleChatMessageReaction: async (messageId, userId) => {
    try {
      // Get the current message
      const message = get().chatMessages.find(m => m.id === messageId);
      if (!message) throw new Error('Message not found');
      
      // Check if user has already reacted
      const hasReacted = message.reactions.includes(userId);
      
      // Update reactions array
      const newReactions = hasReacted
        ? message.reactions.filter(id => id !== userId)
        : [...message.reactions, userId];
      
      // Update in database
      const { error } = await supabase
        .from('chat_messages')
        .update({ reactions: newReactions })
        .eq('id', messageId);
      
      if (error) throw error;
      
      // Update local state
      set(state => ({
        chatMessages: state.chatMessages.map(m => 
          m.id === messageId ? { ...m, reactions: newReactions } : m
        )
      }));
    } catch (error: any) {
      console.error('Error toggling chat message reaction:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Expenses
  addExpense: async (expense) => {
    try {
      const { data, error } = await supabase
        .from('expenses')
        .insert([expense])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        expenses: [...state.expenses, data]
      }));
    } catch (error: any) {
      console.error('Error adding expense:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateExpense: async (id, expense) => {
    try {
      const { error } = await supabase
        .from('expenses')
        .update(expense)
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        expenses: state.expenses.map(e => 
          e.id === id ? { ...e, ...expense } : e
        )
      }));
    } catch (error: any) {
      console.error('Error updating expense:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteExpense: async (id) => {
    try {
      const { error } = await supabase
        .from('expenses')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        expenses: state.expenses.filter(e => e.id !== id)
      }));
    } catch (error: any) {
      console.error('Error deleting expense:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Saved Addresses
  addSavedAddress: async (address) => {
    try {
      const { data, error } = await supabase.from('saved_addresses').insert([address]).select().single();
      if (error) throw error;
      set(state => ({ savedAddresses: [...state.savedAddresses, data] }));
      return data;
    } catch (error: any) {
      console.error('Error adding saved address:', error);
      get().setError(error.message);
      throw error;
    }
  },
  updateSavedAddress: async (id, address) => {
    try {
      const { error } = await supabase.from('saved_addresses').update(address).eq('id', id);
      if (error) throw error;
      set(state => ({
        savedAddresses: state.savedAddresses.map(a => a.id === id ? { ...a, ...address } : a)
      }));
    } catch (error: any) {
      console.error('Error updating saved address:', error);
      get().setError(error.message);
      throw error;
    }
  },
  deleteSavedAddress: async (id) => {
    try {
      const { error } = await supabase.from('saved_addresses').delete().eq('id', id);
      if (error) throw error;
      set(state => ({ savedAddresses: state.savedAddresses.filter(a => a.id !== id) }));
    } catch (error: any) {
      console.error('Error deleting saved address:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Utility functions
  calculateEntityBalance: (entityId, entityType) => {
    const { invoices, payments } = get();
    let totalInvoices = 0;
    let totalPayments = 0;
    let totalCreditNotes = 0;
    
    // Get all invoices for this entity
    const entityInvoices = invoices.filter(
      inv => inv.entity_id === entityId && 
             inv.entity_type === entityType &&
             !inv.voided
    );
    
    // Calculate total invoice amount
    totalInvoices = entityInvoices.reduce((sum, inv) => sum + inv.total, 0);
    
    // Get all payments for this entity's invoices
    let entityPayments = payments.filter(payment => {
      if (payment.invoice_id) {
        // Payment associated with an invoice
        const invoice = invoices.find(inv => inv.id === payment.invoice_id);
        return invoice && 
               invoice.entity_id === entityId && 
               invoice.entity_type === entityType &&
               !payment.voided;
      } else {
        // Account payment - check reference
        return payment.reference === `${entityType}:${entityId}` && !payment.voided;
      }
    });
    
    // Get all credit notes for this entity
    const entityCreditNotes = payments.filter(payment => {
      if (payment.invoice_id && payment.invoice_type === 'credit_note') {
        // Credit note associated with an invoice
        const invoice = invoices.find(inv => inv.id === payment.invoice_id);
        return invoice && 
               invoice.entity_id === entityId && 
               invoice.entity_type === entityType &&
               !payment.voided;
      }
      return false;
    });
    
    // Calculate totals
    totalPayments = entityPayments.filter(p => !p.invoice_type || p.invoice_type !== 'credit_note')
      .reduce((sum, payment) => sum + payment.amount, 0);
    
    totalCreditNotes = entityCreditNotes.reduce((sum, creditNote) => sum + creditNote.amount, 0);
    
    // Calculate balance
    return totalInvoices - totalPayments - totalCreditNotes;
  }
}));